import React, { FunctionComponent } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { List, ListItem, Typography } from '@mui/material';

import { LayoutModel } from '@paragon/contract-library';
import {
  ComponentDesignSchema,
  editLayoutModelProperties,
  getFlattenedChildrenWithCalculatedQuantity,
  getSchemaForProjectHierarchyModel,
  ModelProperty,
  ModelSchema,
} from '@paragon/layout-models';
import { humanCase } from '@paragon/utilities';

import { getSelectedModel } from '../../../store/selectors/grid';
import { RootState } from '../../../store/slices';
import PropertiesContainer from './PropertiesContainer';
import { QuantityField } from './QuantityField';

const EmptyItemContainer = () => (
  <PropertiesContainer>
    <Typography variant="body2">Please add a Project or select an item</Typography>
  </PropertiesContainer>
);

type StateProps = ReturnType<typeof mapStateToProps>;

type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps;

function getItemSchema(model: LayoutModel): ModelSchema<LayoutModel> {
  if (model.type === 'componentDesign') return ComponentDesignSchema as unknown as ModelSchema<LayoutModel>;
  return getSchemaForProjectHierarchyModel(model);
}

export const UnconnectedItemProperties: FunctionComponent<Props> = ({
  editLayoutModelProperties,
  getFlattenedChildrenWithCalculatedQuantity,
  model,
  selectedParentGuid,
}) => {
  if (model == null) {
    return <EmptyItemContainer />;
  }

  const itemSchema = getItemSchema(model);

  let propertyFields = Object.keys(itemSchema.properties).map((propertyName) => (
    <ListItem sx={{ width: '50%' }} key={propertyName}>
      <ModelProperty
        property={propertyName as keyof LayoutModel}
        model={model}
        onEdit={editLayoutModelProperties}
        modelSchema={itemSchema}
      />
    </ListItem>
  ));

  if (selectedParentGuid) {
    propertyFields.push(
      <ListItem sx={{ width: '50%' }} key="quantity">
        <QuantityField model={model} parentGuid={selectedParentGuid} />
      </ListItem>,
    );
  }

  propertyFields.push(
    <ListItem key="quantity-count-header">
      <Typography sx={{ pl: 0, pb: 0 }} variant="h6">
        Quantity Counts
      </Typography>
    </ListItem>,
  );

  const quantityCounts = getFlattenedChildrenWithCalculatedQuantity(model.guid);

  propertyFields = propertyFields.concat(
    Object.values(quantityCounts).map((quantityCountChild, index) => (
      <ListItem sx={{ width: '50%' }} key={`${quantityCountChild.model.guid}-${index}`}>
        <Typography sx={{ pl: 0, pb: 0 }}>
          {humanCase(quantityCountChild.model.type)} {quantityCountChild.model.name} ({quantityCountChild.quantity})
        </Typography>
      </ListItem>
    )),
  );

  return (
    <PropertiesContainer>
      <List sx={{ display: 'flex', flexWrap: 'wrap' }}>{propertyFields}</List>
    </PropertiesContainer>
  );
};

const mapStateToProps = (state: RootState) => ({
  getFlattenedChildrenWithCalculatedQuantity: getFlattenedChildrenWithCalculatedQuantity(state),
  model: getSelectedModel(state),
  selectedParentGuid: state.grid.selectedParentGuid,
});

const actionCreators = {
  editLayoutModelProperties,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedItemProperties);
